import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';

import type { PostFeedTypeFeature } from '@/features/feature-management/types/postTypeFeature';
import type {
  PostCreationChannelOption,
  PostCreationOptions,
  SenderPostCreationOption
} from '@/features/post-editor/types/postCreationOptions';
import { PostFeedType } from '@/features/posts/types/postFeed';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';

export const getSenderChannelTypes = (
  channels: readonly PostCreationChannelOption[]
): PostFeedType[] =>
  [...channels]
    .sort((a, b) => a.orderValue - b.orderValue)
    .map(({ channelType }) => channelType);

export const getAllowedMarketPostTypes = (
  channels: readonly PostCreationChannelOption[]
): ClientPostTypeEnum[] => {
  const postTypes = channels.find(
    (channel) => channel.channelType === PostFeedType.MARKET
  )?.postTypes;
  return [ClientPostTypeEnum.Offer, ClientPostTypeEnum.Seeking].filter((type) =>
    postTypes?.includes(type)
  );
};

/**
 * Resolves backend-authorized sender channels to their UI metadata. The
 * metadata supplies labels, icons' ordering and presentation only; it does
 * not decide whether a sender may use a channel.
 */
export const getSenderChannelOptions = (
  features: PostFeedTypeFeature[],
  channelOptions: readonly PostCreationChannelOption[]
): PostFeedTypeFeature[] => {
  const featuresByType = new Map(
    features.map((feature) => [feature.type, feature])
  );
  return [...channelOptions]
    .sort((a, b) => a.orderValue - b.orderValue)
    .flatMap((option) => {
      const feature = featuresByType.get(option.channelType);
      return feature ? [{ ...feature, orderValue: option.orderValue }] : [];
    });
};

export const getSenderChannelDisplayName = (
  channelOptions: readonly PostFeedTypeFeature[],
  channelType: PostFeedType,
  localizedFallback: string
): string =>
  channelOptions.find((option) => option.type === channelType)?.channelName ??
  localizedFallback;

export const getSenderChannelOptionsForUser = (
  options: PostCreationOptions | undefined
): PostCreationChannelOption[] =>
  options?.senders.find((sender) => sender.senderType === SenderType.USER)
    ?.channels ?? [];

export const getAvailableSenderOptions = (
  options: PostCreationOptions | undefined
): SenderPostCreationOption[] =>
  options?.senders.filter(
    (sender) =>
      sender.channels.length > 0 &&
      (sender.senderType === SenderType.USER || !!sender.organization?.id)
  ) ?? [];

export const getSenderOption = (
  options: readonly SenderPostCreationOption[],
  sender: Sender
): SenderPostCreationOption | undefined =>
  options.find((option) =>
    sender.type === SenderType.USER
      ? option.senderType === SenderType.USER
      : option.senderType === SenderType.ORGANIZATION &&
        option.organization?.id === sender.organizationId
  );

export const selectSenderOption = (
  options: readonly SenderPostCreationOption[],
  savedSender: Sender,
  requestedChannel?: PostFeedType,
  manuallySelected = false
): SenderPostCreationOption | undefined => {
  const preferred =
    !manuallySelected && requestedChannel
      ? options.find((option) =>
          option.channels.some(
            (channel) => channel.channelType === requestedChannel
          )
        )
      : undefined;
  const saved = getSenderOption(options, savedSender);
  if (
    saved &&
    (manuallySelected ||
      !preferred ||
      saved.channels.some(
        (channel) => channel.channelType === requestedChannel
      ))
  ) {
    return saved;
  }
  return (
    preferred ??
    options.find((option) => option.senderType === SenderType.USER) ??
    options[0]
  );
};
